import loadImage from "blueimp-load-image";
import { MediaUploadDto } from "collaboration-service";
import { SelectFileModuleProps } from "components/FileSelector";
import { LinkDto } from "imaginarity-azure";
import { belowBreakpointOrEqual, Button, ButtonProps, Image, ImgThemeConsumer, Loader } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from 'react';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import { canvasToFile, getLink, reducerBatchJob, reducerSetIfChanged } from "services/Helpers";
import { useCheckWhereIAm } from "services/Helpers/RoutingHelper";
import { getTranslated } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { cmsTheme } from "views/CMS/CMSTheme";
import FileSelector, { getAllowedFileExtensions, MediaType } from "./FileSelector";
import ImageEdit from "./ImageEdit";
import { MediaSelectionSC as T } from './MediaSelectionSC';

export interface Media {
    mediaType: string;
    width?: number;
    height?: number;
    changed: boolean;
    fileName: string;
    links?: LinkDto[];
}

export interface MediaSelectionModuleProps extends Omit<SelectFileModuleProps, "filesSelected"> {
    uploadType: MediaType;
}

export interface MSProps<T> {
    onMediaSelected: (file: File, mediaType: MediaUploadDto) => void;
    imgAspect?: number;
    uploadType: MediaType;
    width?: string;
    backgroundColor?: string;
    border?: string;
    noImageSelected?: boolean;
    hideButtonText?: boolean;
    showPlaceholderImage?: boolean;
    media?: T;
    refName?: string;
    onDelete?: () => void;
    marginTop?: number;
    additionalButtons?: ButtonProps[];
    darkMode?: boolean;
    editImage?: boolean;
    children?: (p: MediaSelectionModuleProps) => JSX.Element;
    hotSpotMode?: boolean;
    allowQuicktime?: boolean;
}

export interface MSState {
    file?: File; // Selected File 
    fileUrl?: string; // Url of the original file, shall only change on new file loaded 
    fileUrlTmp?: string; // copy of fileUrl, to be able to reset it back on "cancel"
    editImage: boolean;
    type: MediaType | undefined;
    width: number;
    height: number;
    filename?: string;
    active?: boolean;
}

const calcType = <T extends Media>(acceptedType?: MediaType, media?: T): MediaType | undefined => {
    let toRet: MediaType | undefined = undefined;
    if (!media?.mediaType)
        toRet = undefined;
    else if (acceptedType && acceptedType === MediaType.IMAGE && media.mediaType.startsWith('image/svg+xml'))
        toRet = MediaType.IMAGE;
    else if (((acceptedType && acceptedType === MediaType.SVG) || acceptedType === undefined) && media.mediaType.startsWith('image/svg+xml'))
        toRet = MediaType.SVG;
    else if (media.mediaType.startsWith("image"))
        toRet = MediaType.IMAGE;
    else if (media.mediaType.startsWith("video"))
        toRet = MediaType.VIDEO;
    else if (media.mediaType.startsWith("audio"))
        toRet = MediaType.AUDIO;
    return acceptedType === MediaType.ALL ? toRet : (acceptedType ?? toRet);
}

const initState = (): MSState => ({
    editImage: false,
    height: 1,
    type: undefined,
    width: 0,
});

type MSActions =
    | { type: "batch", value: MSActions[] }
    | { type: "set_file", value?: File }
    | { type: "set_editImage", value: boolean }
    | { type: "set_width_height", width: number, height: number }
    | { type: "set_type", value: MediaType | undefined }
    | { type: "set_file_name", value?: string }
    | { type: "set_file_url", value?: string }
    | { type: "set_file_url_tmp", value?: string }
    | { type: "set_active", value?: boolean }
    ;

const reducer = (state: MSState, action: MSActions): MSState => {
    switch (action.type) {
        case "batch":
            return reducerBatchJob(state, action.value, reducer);
        case "set_file":
            if (!_.isEqual(state.file, action.value))
                return { ...state, file: action.value };
            break;
        case "set_editImage":
            return reducerSetIfChanged(state, "editImage", action.value);
        case "set_width_height":
            if (state.width !== action.width || state.height !== action.height)
                return { ...state, width: action.width, height: action.height };
            break;
        case "set_type":
            return reducerSetIfChanged(state, "type", action.value);
        case "set_file_name":
            return reducerSetIfChanged(state, "filename", action.value);
        case "set_file_url":
            return reducerSetIfChanged(state, "fileUrl", action.value);
        case "set_file_url_tmp":
            return reducerSetIfChanged(state, "fileUrlTmp", action.value);
        case "set_active":
            return reducerSetIfChanged(state, "active", action.value);
    }
    return state;
}

const calcNeedCrop = (height: number, width: number, targetAspect: number | undefined): boolean => {
    if (!targetAspect) return false;
    const imgAspect = Math.floor((width / height) * 100.0);
    const aspect = Math.floor(targetAspect * 100.0);
    const epsilon = 0.0001;
    return Math.abs(imgAspect - aspect) > epsilon;
}

const getSVGDim = (media: Media | undefined, imgAspect: number | undefined) => {
    const propsAspect = imgAspect ?? 1;
    const mediaAspect = (media && media.width && media.height && media.width !== 0 && media.height !== 0) ? media.width / media.height : 1;
    return {
        width: propsAspect < 1 ? "100%" : (mediaAspect < 1 ? (100 / propsAspect) + "%" : "100%"),
        height: propsAspect < 1 ? (mediaAspect < 1 ? "100%" : (100 / propsAspect) + "%") : "100%",
    }
}

const defineImageAspect = (media: Media | undefined, imgAspect: number | undefined, width: number, height: number): number => {
    if (imgAspect)
        return imgAspect;
    if (width !== 0 && height !== 0)
        return width / height;
    if (media && media.width && media.height && media.width !== 0 && media.height !== 0)
        return media.width / media.height;
    return 1;
};

const MediaSelection = <T extends Media>(p: MSProps<T>) => {

    const { onMediaSelected, editImage: pEditImage, uploadType, media, refName, imgAspect, additionalButtons, darkMode: pDarkMode, marginTop,
        backgroundColor, border, hideButtonText, onDelete: pOnDelete, showPlaceholderImage, width: pWidth, hotSpotMode,
        children, allowQuicktime } = p;
    const [state, dp] = React.useReducer(reducer, initState());
    const { file, width, height, fileUrl, fileUrlTmp, editImage, type, active } = state;
    const { t } = useImgI18N("general");

    React.useEffect(() => {
        dp({
            type: "batch", value: [
                { type: "set_type", value: calcType(uploadType, media) },
                { type: "set_file" },
                { type: "set_file_url", value: calcType(undefined, media) === calcType(uploadType, media) ? _.find(media?.links, l => l.ref === (refName ?? "self"))?.uri : undefined, }
            ]
        });
    }, [dp, uploadType, media, refName]);

    React.useEffect(() => {
        dp(
            { type: "set_editImage", value: pEditImage ?? false })
    }, [dp, pEditImage]);

    const fileSelected = React.useMemo(() => (newFile: File | undefined, mediaUpload: MediaUploadDto) => {
        // console.log("MediaSelection.tsx::184 => mediaUpload", mediaUpload);
        const link = getLink(mediaUpload as any, "self")?.uri
        if (link) {
            dp({
                type: "batch", value: [
                    { type: "set_file" },
                    { type: "set_file_url", value: link },
                    { type: "set_width_height", width: mediaUpload.width ?? 0, height: mediaUpload.height ?? 1 },
                    // really?
                    { type: "set_editImage", value: false }
                ]
            })
            onMediaSelected(newFile as any, mediaUpload);
        }
    }, [onMediaSelected]);

    const onFileSelected = React.useMemo(() => async (newFile: File | undefined, type: MediaType) => {
        if (!newFile)
            return;
        if (!allowQuicktime && newFile.type.endsWith('quicktime')) // mov cannot be selected
            return;
        const set = (currentFile: File | undefined, width: number, height: number) => {
            if (currentFile) {
                const MUDto = {
                    changed: true,
                    fileName: currentFile.name,
                    mediaType: currentFile.type,
                    reference: "",
                    thumbnails: [],
                    width: Math.floor(width),
                    height: Math.floor(height),
                    links: [{
                        ref: "self",
                        uri: URL.createObjectURL(currentFile),
                        method: "GET"
                    }]
                } as MediaUploadDto;
                dp({ type: "set_active", value: false });
                onMediaSelected(currentFile, MUDto);
            }
        }

        const setSVG = (file: File, type: MediaType) => {
            var rdr = new FileReader();
            rdr.onload = (e) => {
                var xml = rdr.result;
                //console.log('xml', xml);
                var d = document.createElement('div');
                d.innerHTML = xml as string;
                var s = d.getElementsByTagName('svg')[0];
                const w = s.getAttribute('viewBox') !== null ? s.viewBox.baseVal.width : s.getAttribute('width') !== null ? s.width.baseVal.value : 100;
                const h = s.getAttribute('viewBox') !== null ? s.viewBox.baseVal.height : s.getAttribute('height') !== null ? s.height.baseVal.value : 100;
                dp({
                    type: "batch", value: [
                        { type: "set_editImage", value: false },
                        { type: "set_type", value: type },
                        { type: "set_file", value: file },
                        { type: "set_file_url", value: URL.createObjectURL(file) },
                        {
                            type: "set_width_height",
                            width: w,
                            height: h,
                        }
                    ]
                })
                set(file, w, h);
            }
            rdr.readAsText(file);
            dp({ type: "set_active", value: false });
        };

        const setTiff = (file: File, type: MediaType) => {

        };

        switch (type) {
            case MediaType.IMAGE:
                if (newFile.type.endsWith('svg+xml')) {
                    setSVG(newFile, type);
                }
                else if (newFile.type.endsWith('tiff')) {
                    setTiff(newFile, type);
                }
                else {
                    loadImage(newFile, async (data: HTMLCanvasElement | HTMLImageElement | Event) => {
                        const canvas = data as HTMLCanvasElement;
                        const needCrop = calcNeedCrop(canvas.height, canvas.width, imgAspect);
                        const loadedFile = await canvasToFile(canvas, newFile.name,);
                        dp({
                            type: "batch", value: [
                                { type: "set_file", value: loadedFile },
                                { type: "set_file_url", value: URL.createObjectURL(loadedFile) },
                                { type: "set_file_url_tmp", value: fileUrl },
                                { type: "set_editImage", value: needCrop },
                                { type: "set_type", value: type },
                                { type: "set_width_height", width: canvas.width, height: canvas.height },
                                // ev. ausserhalb?
                                { type: "set_file_name", value: newFile.name }
                            ]
                        });
                        if (!needCrop)
                            set(loadedFile, canvas.width, canvas.height);
                    }, { orientation: true, canvas: true });
                }
                break;
            case MediaType.SVG:
                setSVG(newFile, type);
                break;
            default:
                dp({
                    type: "batch", value: [
                        { type: "set_editImage", value: false },
                        { type: "set_type", value: type },
                        { type: "set_file", value: newFile },
                        { type: "set_file_url", value: URL.createObjectURL(newFile) },
                    ]
                });
                set(newFile, 0, 1);
                break;
        }

    }, [imgAspect, onMediaSelected, fileUrl, allowQuicktime]);


    const oFS = React.useMemo(() => async (newFile: File | undefined, type: MediaType) => {
        //console.log("---- X")
        dp({ type: "set_active", value: true });
        await onFileSelected(newFile, type);
        //console.log("---- Y")
    }, [onFileSelected]);

    const onFilesSelected = React.useMemo(() => (files: File[] | undefined, type: MediaType): void => {
        switch (type) {
            case MediaType.IMAGE:
                break;
            case MediaType.SVG:
                break;
            case MediaType.VIDEO:
                break;
            case MediaType.AUDIO:
                break;
        }
    }, []);

    const { selectImage, selectAudio, selectSVG, selectVideo } = React.useMemo(() => {
        const fs = new FileSelector({ onFileSelected: oFS, onFilesSelected, multipleFiles: false });
        return {
            selectImage: () => fs.openFileSelect(MediaType.IMAGE),
            selectSVG: () => fs.openFileSelect(MediaType.SVG),
            selectVideo: () => fs.openFileSelect(MediaType.VIDEO),
            selectAudio: () => fs.openFileSelect(MediaType.AUDIO)
        }
    }, [oFS, onFilesSelected])

    const onDrop = React.useMemo(() => (files: File[], fileRejections: FileRejection[], event: DropEvent) => {
        //console.log("files => " + JSON.stringify(files));
        if (files.length > 0) {
            const f = files[0];
            const mediaType = f.type.startsWith('video') ? MediaType.VIDEO :
                f.type.startsWith('audio') ? MediaType.AUDIO :
                    f.type.includes('image/svg+xml') ? MediaType.SVG :
                        f.type.startsWith('image') ? MediaType.IMAGE :
                            undefined;

            //console.log(`uploadType =${uploadType}, mediaType = ${mediaType}`);
            if (mediaType !== undefined && (uploadType === mediaType || mediaType === MediaType.SVG || uploadType === MediaType.ALL)) {
                //console.log("calling...");
                oFS(f, mediaType);
            }
        }
    }, [oFS, uploadType]);

    const onAbort = React.useMemo(() => () => {
        if (fileUrlTmp !== undefined) {
            dp({
                type: "batch", value: [
                    { type: "set_editImage", value: false },
                    { type: "set_file_url", value: fileUrlTmp },    // reset fileUrl to original value (before selection started)
                    { type: "set_file_url_tmp", value: undefined },
                    { type: "set_active", value: false },
                ]
            });
        }
        else
            dp({
                type: "batch", value: [
                    { type: "set_editImage", value: false },
                    { type: "set_active", value: false },
                    { type: "set_file_url", value: undefined },
                ]
            });
    }, [dp, fileUrlTmp]);

    const onCropFinish = React.useMemo(() => (data: Blob | string, width: number, height: number) => {
        if (typeof data === "string") {
            dp({
                type: "batch", value: [
                    { type: "set_file_url", value: data },
                    { type: "set_editImage", value: false },
                ]
            });
        }
        if (typeof data === "object") {
            const MUDto = {
                changed: true,
                fileName: media?.fileName ?? "unknown_file_name",
                mediaType: data.type,
                reference: "",
                thumbnails: [],
                width: Math.floor(width),
                height: Math.floor(height),
                links: [{
                    ref: "self",
                    method: "GET",
                    uri: fileUrl
                }]
            } as MediaUploadDto;
            onMediaSelected(data as File, MUDto);
        }
        dp({ type: "set_active", value: false });

    }, [onMediaSelected, media, fileUrl]);

    const onEdit = React.useMemo(() => () => {
        const blob = new Blob([], { type: "" });
        const newFile = new File([blob], "dummy", { lastModified: new Date().getTime(), type: blob.type });
        dp({
            type: "batch", value: [
                { type: "set_editImage", value: true },
                { type: "set_type", value: MediaType.IMAGE },
                { type: "set_file", value: file ?? newFile }
            ]
        });
    }, [dp, file]);

    const onDelete = React.useMemo(() => () => {
        dp({
            type: "batch", value: [
                { type: "set_file_url" },
                { type: "set_file" }
            ]
        })
        if (pOnDelete)
            pOnDelete();
        dp({ type: "set_active", value: false });
    }, [pOnDelete]);




    const showAllMedia = uploadType === MediaType.ALL;
    const showOnlyImage = uploadType === MediaType.IMAGE;
    const showOnlySVG = uploadType === MediaType.SVG;
    const showOnlyVideo = uploadType === MediaType.VIDEO;
    const showOnlyAudio = uploadType === MediaType.AUDIO;
    const darkMode = pDarkMode ?? false;
    const accept = (showOnlyImage ? "image/.," : showOnlyVideo ? 'video/.,' : showOnlyAudio ? 'audio/.,' : '') + getAllowedFileExtensions(uploadType);

    const check = useCheckWhereIAm();
    const isMoments = check('community_add_post');
    const buttonUploadImage = hotSpotMode ? undefined : <Button icon="upload image" floated="left" content={hideButtonText ? undefined : "image"} kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={selectImage} />;
    const buttonUploadSVG = (hotSpotMode || isMoments) ? undefined : <Button icon="upload image" floated="left" content={hideButtonText ? undefined : "svg"} kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={selectSVG} />;
    const buttonUploadVideo = <Button icon="upload video" floated="left" content={hideButtonText ? undefined : "video"} kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={selectVideo} />;
    const buttonUploadAudio = <Button icon="upload audio" floated="left" content={hideButtonText ? undefined : "audio"} kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={selectAudio} />;
    const buttonDelete = hotSpotMode ? undefined : <Button icon="delete" floated="right" kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={onDelete} />;
    const buttonEdit = hotSpotMode ? undefined : <Button icon="edit" floated="right" kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} onClick={onEdit} />;

    const url = fileUrl; // calcType(undefined, this.props.media) === calcType(this.props.uploadType, this.props.media) ? this.state.fileUrl : undefined;
    const isSVG = file?.type.endsWith("svg+xml") ?? media?.mediaType?.endsWith("svg+xml") ?? false;
    const aspect = defineImageAspect(media, imgAspect, width, height);
    const svgDim = isSVG ? getSVGDim(media, imgAspect) : { width: "", height: "" };

    return (
        <ImgThemeConsumer>
            {
                (theme) => {
                    const isMobileDevice = belowBreakpointOrEqual({ theme }, "tablet");
                    return (
                        <T.MSContainer
                            width={pWidth ?? "100%"}
                            aspect={imgAspect ?? (16 / 9)}
                            background={backgroundColor ?? "transparent"}
                            border={border ?? "none"}
                        >
                            {!url ?
                                <T.ImageEditContainer>
                                    <Dropzone accept={accept} onDrop={onDrop} >
                                        {({ getRootProps, getInputProps }) => (
                                            <section className="DropzoneContainer">
                                                <div {...getRootProps({ className: 'dropzone' })} /*style={{ border: "1px dashed gray" }} */>
                                                    <input {...getInputProps()} />
                                                    {showPlaceholderImage && !isMobileDevice &&
                                                        < Image marginTop={marginTop} src={theme.dropZoneSvg} />
                                                    }
                                                    {showPlaceholderImage && isMobileDevice &&
                                                        <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9" }}>
                                                            <div style={{ position: "absolute", inset: 0 }}>
                                                                < Image marginTop={marginTop} src={cmsTheme.emptyImageUrl} height="200px" />
                                                            </div>
                                                            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", fontSize: "1.5em" }}>
                                                                {t("click to select media")}
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            </section>
                                        )}
                                    </Dropzone>
                                    <T.ButtonBarImage darkMode={darkMode} notSelected>
                                        {(showAllMedia || showOnlyImage) && buttonUploadImage}
                                        {(showAllMedia || showOnlySVG) && buttonUploadSVG}
                                        {(showAllMedia || showOnlyVideo) && buttonUploadVideo}
                                        {(showAllMedia || showOnlyAudio) && buttonUploadAudio}
                                        {children !== undefined ?
                                            children({ fileSelected, uploadType, buttonProps: { kind: darkMode ? "halfTransparentButtonDark" : "halfTransparentButton" } }) : <div />}
                                        {url !== undefined && buttonDelete}
                                        {url && (type === MediaType.IMAGE || type === MediaType.SVG) && buttonEdit}
                                        {_.map(additionalButtons, (props, i) => <Button {...props} kind={darkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} key={i} />)}

                                    </T.ButtonBarImage>
                                </T.ImageEditContainer>
                                :
                                <>
                                    {(type === MediaType.IMAGE || type === MediaType.SVG) &&
                                        <>
                                            {isSVG ?
                                                <div style={{ paddingBottom: `${100 / aspect}%`, width: "100%", position: "relative" }} className="SVG container">
                                                    <object
                                                        data={url}
                                                        width={svgDim.width}
                                                        height={svgDim.height}
                                                        style={{
                                                            transform: `${aspect < 1 ? "translateY(-50%)" : "translateX(-50%)"}`,
                                                            top: `${aspect < 1 ? "50%" : "none"}`,
                                                            left: `${aspect < 1 ? "none" : "50%"}`,
                                                            position: "absolute"
                                                        }}
                                                    > </object>
                                                </div>
                                                :
                                                <div style={{ marginBottom: -5 }}>
                                                    <ImageEdit
                                                        open={editImage === true}
                                                        src={url}
                                                        onCropFinish={onCropFinish}
                                                        onAbort={onAbort}
                                                        aspect={imgAspect}
                                                        additionalMoveButtons
                                                    />
                                                    <Image src={url} />
                                                </div>

                                            }
                                            <T.ButtonBarImage darkMode={darkMode}>
                                                {(showAllMedia || showOnlyImage || showOnlySVG) &&
                                                    <>
                                                        {showOnlyImage && buttonUploadImage}
                                                        {showOnlySVG && buttonUploadSVG}
                                                        {showAllMedia &&
                                                            <>
                                                                {buttonUploadVideo}
                                                                {buttonUploadAudio}
                                                            </>
                                                        }
                                                        {(type === MediaType.IMAGE || type === MediaType.SVG) && url !== undefined &&
                                                            <>
                                                                {buttonDelete}
                                                                {!isSVG && buttonEdit}
                                                                {_.map(additionalButtons, (props, i) => <Button {...props} kind={pDarkMode ? "halfTransparentButtonDark" : "halfTransparentButton"} key={i} />)}
                                                                {children !== undefined ?
                                                                    children({ fileSelected, uploadType, buttonProps: { kind: darkMode ? "halfTransparentButtonDark" : "halfTransparentButton" } }) : <div />}
                                                            </>
                                                        }
                                                    </>
                                                }
                                            </T.ButtonBarImage>
                                        </>
                                    }
                                    {type === MediaType.VIDEO &&
                                        <>
                                            <Image src={theme.emptyImageUrl} />
                                            <T.ImageEditImage>
                                                <video
                                                    controlsList="nodownload"
                                                    width="100%"
                                                    controls
                                                    preload="metadata"
                                                    autoPlay={false}
                                                    crossOrigin="anonymous"
                                                    src={url}
                                                    // src={`${url}#t=0.01`}
                                                    style={{ width: "100%", outline: "none" }}
                                                >
                                                    {_.map((media as any)?.subtitles, (s, idx) => {
                                                        const l = _.find(s.links, l => l.ref === "self");
                                                        const lng = (s.lng ?? "").split("-")[0];
                                                        const label = t(`${lng}`)
                                                        const sdef = getTranslated((media as any)?.subtitles);
                                                        if (l && label) {
                                                            return (
                                                                <track key={s.reference} src={l?.uri} label={label} kind="captions" srcLang={lng} default={sdef !== undefined && sdef.lng === s.lng} />
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </video>
                                            </T.ImageEditImage>
                                            <T.ButtonBarVideo darkMode={darkMode}>
                                                {(showAllMedia || showOnlyVideo) &&
                                                    <>
                                                        {showAllMedia &&
                                                            <>
                                                                {buttonUploadImage}
                                                                {buttonUploadAudio}
                                                            </>
                                                        }
                                                        {buttonUploadVideo}
                                                        {children !== undefined ?
                                                            children({ fileSelected, uploadType, buttonProps: { kind: darkMode ? "halfTransparentButtonDark" : "halfTransparentButton" } }) : <div />}
                                                        {url !== undefined && buttonDelete}
                                                    </>
                                                }
                                            </T.ButtonBarVideo>
                                        </>
                                    }
                                    {type === MediaType.AUDIO &&
                                        <>
                                            <Image src={theme.emptyImageUrl} />
                                            <T.ImageEditImageAudio>
                                                <audio
                                                    controlsList="nodownload"
                                                    controls
                                                    preload="metadata"
                                                    autoPlay={false}
                                                    crossOrigin="anonymous"
                                                    src={url}
                                                    style={{ width: "100%", outline: "none" }}
                                                />
                                            </T.ImageEditImageAudio>
                                            <T.ButtonBarAudio darkMode={darkMode}>
                                                {(showAllMedia || showOnlyAudio) &&
                                                    <>
                                                        {showAllMedia &&
                                                            <>
                                                                {buttonUploadImage}
                                                                {buttonUploadVideo}
                                                            </>
                                                        }
                                                        {buttonUploadAudio}
                                                        {url !== undefined && buttonDelete}
                                                    </>
                                                }
                                            </T.ButtonBarAudio>
                                        </>
                                    }
                                </>
                            }
                            <Loader active={active} infoColor="@accent" infoText={t('loading...')} size="small" />
                        </T.MSContainer>
                    )
                }
            }
        </ImgThemeConsumer >
    );

}
export default MediaSelection;
